import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Clock, Users, Trophy, Upload, ThumbsUp, Medal, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { formatDistanceToNow } from 'date-fns';

export default function ChallengeDetailPage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [entryForm, setEntryForm] = useState({
    media_url: '',
    description: '',
    time_taken_minutes: '',
  });

  useEffect(() => {
    fetchChallenge();
  }, [id]);

  const fetchChallenge = async () => {
    try {
      const [challengeRes, leaderboardRes] = await Promise.all([
        api.get(`/challenges/${id}`),
        api.get(`/challenges/${id}/leaderboard`),
      ]);
      setChallenge(challengeRes.data);
      setLeaderboard(leaderboardRes.data);
    } catch (error) {
      toast.error('Failed to load challenge');
      navigate('/challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.media_url || !entryForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/challenges/${id}/enter`, {
        challenge_id: id,
        media_url: entryForm.media_url,
        description: entryForm.description,
        time_taken_minutes: parseInt(entryForm.time_taken_minutes) || challenge.time_limit_minutes,
      });
      toast.success('Entry submitted! Good luck! 🍀');
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 },
      });
      setShowSubmitDialog(false);
      fetchChallenge();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (entryId) => {
    try {
      const response = await api.post(`/challenges/${id}/vote/${entryId}`);
      toast.success(response.data.voted ? 'Vote added!' : 'Vote removed');
      fetchChallenge();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-muted text-foreground';
  };

  const userHasEntered = challenge?.entries?.some(e => e.user_id === user?.id);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="h-64 w-full rounded-2xl mb-6" />
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (!challenge) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="challenge-detail-page">
      {/* Back */}
      <button
        onClick={() => navigate('/challenges')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Challenges
      </button>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative aspect-video rounded-2xl overflow-hidden mb-6"
      >
        <img
          src={challenge.cover_image || 'https://images.unsplash.com/photo-1583637113765-1d46854b606f?w=1200&q=80'}
          alt={challenge.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-200/90 via-dark-200/40 to-transparent" />
        
        {/* Status */}
        <Badge
          className={`absolute top-4 left-4 ${
            challenge.status === 'active' ? 'bg-mint-500' :
            challenge.status === 'voting' ? 'bg-sidi-500' :
            challenge.status === 'upcoming' ? 'bg-sahara-400' : 'bg-muted-foreground'
          }`}
        >
          {challenge.status === 'active' ? '🔴 Live' :
           challenge.status === 'voting' ? '🗳️ Voting' :
           challenge.status === 'upcoming' ? '⏳ Coming Soon' : '✓ Completed'}
        </Badge>

        {/* Prize */}
        {challenge.prize_amount > 0 && (
          <div className="absolute top-4 right-4 bg-sahara-400 text-dark-100 px-4 py-2 rounded-full font-bold">
            🏆 {challenge.prize_amount} TND Prize
          </div>
        )}

        {/* Title */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h1 className="font-cairo text-3xl md:text-4xl font-black text-white mb-2">
            {challenge.title}
          </h1>
          <p className="text-white/80">{challenge.description}</p>
        </div>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4 text-center">
          <Timer className="w-6 h-6 mx-auto mb-2 text-harissa-500" />
          <p className="font-cairo font-bold text-xl">{challenge.time_limit_minutes}m</p>
          <p className="text-sm text-muted-foreground">Time Limit</p>
        </Card>
        <Card className="p-4 text-center">
          <Users className="w-6 h-6 mx-auto mb-2 text-sidi-500" />
          <p className="font-cairo font-bold text-xl">{challenge.participants_count}</p>
          <p className="text-sm text-muted-foreground">Participants</p>
        </Card>
        <Card className="p-4 text-center">
          <Trophy className="w-6 h-6 mx-auto mb-2 text-sahara-400" />
          <p className="font-cairo font-bold text-xl">{challenge.entries_count}</p>
          <p className="text-sm text-muted-foreground">Entries</p>
        </Card>
        <Card className="p-4 text-center">
          <ThumbsUp className="w-6 h-6 mx-auto mb-2 text-mint-500" />
          <p className="font-cairo font-bold text-xl">{challenge.votes_count}</p>
          <p className="text-sm text-muted-foreground">Total Votes</p>
        </Card>
      </div>

      {/* Rules */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="font-cairo font-bold text-xl mb-4">Challenge Rules</h2>
          <ul className="space-y-2">
            {challenge.rules?.map((rule, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-harissa-500/20 text-harissa-500 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                  {index + 1}
                </span>
                <span>{rule}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Join Challenge CTA */}
      {challenge.status === 'active' && !userHasEntered && (
        <Card className="mb-8 bg-gradient-to-r from-harissa-500 to-harissa-600 text-white">
          <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-cairo font-bold text-xl mb-1">Ready to compete?</h3>
              <p className="text-white/80">Submit your entry before time runs out!</p>
            </div>
            <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
              <DialogTrigger asChild>
                <Button className="bg-white text-harissa-500 hover:bg-white/90 font-bold" data-testid="submit-entry-btn">
                  <Upload className="mr-2 w-4 h-4" />
                  Submit Entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Your Entry</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitEntry} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Photo/Video URL *</label>
                    <Input
                      placeholder="https://..."
                      value={entryForm.media_url}
                      onChange={(e) => setEntryForm({ ...entryForm, media_url: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Description *</label>
                    <Textarea
                      placeholder="Tell us about your dish..."
                      value={entryForm.description}
                      onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Time Taken (minutes)</label>
                    <Input
                      type="number"
                      placeholder={`Max: ${challenge.time_limit_minutes}`}
                      value={entryForm.time_taken_minutes}
                      onChange={(e) => setEntryForm({ ...entryForm, time_taken_minutes: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full btn-mharher" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Entry'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      {userHasEntered && (
        <Card className="mb-8 border-mint-500/30 bg-mint-500/10">
          <CardContent className="p-6 text-center">
            <Medal className="w-12 h-12 mx-auto mb-3 text-mint-500" />
            <h3 className="font-cairo font-bold text-xl mb-1">You're in the competition! 🎉</h3>
            <p className="text-muted-foreground">Good luck! Check the leaderboard below.</p>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <div>
        <h2 className="font-cairo font-bold text-2xl mb-4 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-sahara-400" />
          Leaderboard
        </h2>

        {leaderboard.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No entries yet. Be the first!</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {leaderboard.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`overflow-hidden ${entry.user_id === user?.id ? 'border-harissa-500' : ''}`}>
                  <div className="flex items-center gap-4 p-4">
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getRankStyle(entry.rank || index + 1)}`}>
                      {entry.rank || index + 1}
                    </div>

                    {/* User */}
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={entry.profile_picture} />
                      <AvatarFallback className="bg-harissa-500 text-white font-bold">
                        {entry.username?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{entry.display_name}</p>
                      <p className="text-sm text-muted-foreground truncate">{entry.description}</p>
                    </div>

                    {/* Time */}
                    <div className="text-center px-4 hidden sm:block">
                      <p className="font-bold">{entry.time_taken_minutes}m</p>
                      <p className="text-xs text-muted-foreground">Time</p>
                    </div>

                    {/* Votes */}
                    <div className="text-center">
                      <p className="font-cairo font-bold text-xl text-harissa-500">{entry.votes_count}</p>
                      <p className="text-xs text-muted-foreground">Votes</p>
                    </div>

                    {/* Vote Button */}
                    {(challenge.status === 'active' || challenge.status === 'voting') && entry.user_id !== user?.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVote(entry.id)}
                        data-testid={`vote-entry-${entry.id}`}
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Entry Image */}
                  {entry.media_url && (
                    <div className="border-t border-border">
                      <img
                        src={entry.media_url}
                        alt={`Entry by ${entry.username}`}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
